import { useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useState, type CSSProperties } from 'react';
import { useGameStore } from '@/stores/gameStore';
import { useStudentAutoNavigation } from '@/hooks/useStudentAutoNavigation';
import { useCurrentSession, useGameSessionStore } from '@/stores/gameSessionStore';
import { audioManager } from '@/utils/audioManager';
import { PLAYER_SOUNDS } from '@/config/soundEffects';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import MobileGameOverlay from '@/components/MobileGameOverlay';
import { scaleFontSize } from '@/utils/scaleFontSize';
import { clearStoredSessionData } from '@/utils/sessionRecovery';
import {
  getStoredEmpiricalStudySession,
  updateEmpiricalStudySession
} from '@/utils/empiricalStudySession';
import { empiricalStudyService } from '@/services/study/empiricalStudyService';

const SCORE_RESULT_FONT_SCALE = 1.2;

const ScoreScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useSafeNavigate();
  const location = useLocation();
  const currentSession = useCurrentSession();
  const playerId = searchParams.get('playerId');
  const sessionId = searchParams.get('sessionId');
  const activeSession = sessionId ? currentSession : null;
  const forcedSpectator = searchParams.get('spectator') === 'true';
  const gameMode = useGameStore(state => state.gameMode);
  const isAiMode = gameMode === 'ai';
  const isSessionDriven = !isAiMode && Boolean(sessionId);
  const isSpectatorView = forcedSpectator || (!playerId && !isAiMode);
  const resetSessionStore = useGameSessionStore(state => state.reset);

  // Enable student auto-navigation to respond to teacher's session state changes
  useStudentAutoNavigation(isSessionDriven);
  const {
    getCurrentQuestion,
    selectedAnswer,
    currentQuestionIndex,
    getTotalQuestions,
    gameState,
    setGameState,
    players,
    getCumulativeScores,
    finalizeRoundScores,
    scoringConfig,
    currentPlayer,
    currentPlayerId,
    setCurrentPlayerId,
    advanceToNextStage,
    getNextStage,
    resetGame
  } = useGameStore();
  const isFinalScoresRoute = location.pathname === '/final-scores';
  const [showPlayerResult, setShowPlayerResult] = useState(!isSpectatorView);
  const [showLeaderboard, setShowLeaderboard] = useState(isSpectatorView);
  const [studySession, setStudySession] = useState(() =>
    getStoredEmpiricalStudySession(sessionId || undefined)
  );

  const currentQuestion = getCurrentQuestion();
  const totalQuestions = getTotalQuestions();
  const resultScaledFontSize = (value: string) => scaleFontSize(value, SCORE_RESULT_FONT_SCALE);
  const isScoreResultView = showPlayerResult && !showLeaderboard;

  // Get detailed scoring information
  const cumulativeScores = getCumulativeScores();
  const activePlayer = isSpectatorView
    ? null
    : currentPlayer
      ?? (currentPlayerId ? players.find(p => p.id === currentPlayerId) || null : null)
      ?? players.find(p => p.id === 'current-player')
      ?? null;

  // CRITICAL FIX: Use activePlayer.lastAnswerIndex (persisted with player data)
  // instead of selectedAnswer (gets cleared during navigation)
  // Fallback to selectedAnswer for backwards compatibility
  const playerSelectedAnswer = isSessionDriven
    ? activePlayer?.lastAnswerIndex
    : activePlayer?.lastAnswerIndex ?? selectedAnswer;
  const isCorrect = playerSelectedAnswer === currentQuestion?.correctAnswerIndex;
  const nextStage = getNextStage();
  const isFinalStage = !nextStage;

  // IMPORTANT: All hooks must be called BEFORE any conditional returns
  // Set game state to scoring and finalize scores when component mounts
  useEffect(() => {
    if (isSessionDriven) {
      return;
    }

    if (!isFinalScoresRoute && gameState !== 'scoring') {
      setGameState('scoring');
    }

    if (isFinalScoresRoute && gameState !== 'finished') {
      setGameState('finished');
    }

    finalizeRoundScores();
  }, [gameState, setGameState, finalizeRoundScores, isFinalScoresRoute, isSessionDriven]);

  useEffect(() => {
    if (playerId) {
      setCurrentPlayerId(playerId);
    }
  }, [playerId, setCurrentPlayerId]);

  // Show result sequence - students stay on result view until teacher shows leaderboard
  useEffect(() => {
    // Skip this effect in AI mode - AI players use local progression controls
    if (isAiMode) {
      return;
    }

    // Play correct or wrong answer sound immediately
    if (isSpectatorView) {
      return;
    }

    if (audioManager.isReady()) {
      if (isCorrect) {
        audioManager.play(PLAYER_SOUNDS.CORRECT_ANSWER.id);
      } else {
        audioManager.play(PLAYER_SOUNDS.WRONG_ANSWER.id);
      }
    }

    const sequence = [
      { delay: 0, action: () => setShowPlayerResult(true) }
      // Removed 4-second auto-transition - students wait here for teacher
    ];

    sequence.forEach(({ delay, action }) => {
      setTimeout(action, delay);
    });
  }, [isCorrect, isAiMode, isSpectatorView]);

  useEffect(() => {
    // In AI mode, play the feedback sound once the score screen appears
    if (!isAiMode || isFinalScoresRoute) {
      return;
    }

    if (audioManager.isReady()) {
      if (isCorrect) {
        audioManager.play(PLAYER_SOUNDS.CORRECT_ANSWER.id);
      } else {
        audioManager.play(PLAYER_SOUNDS.WRONG_ANSWER.id);
      }
    }
  }, [isAiMode, isFinalScoresRoute, isCorrect, currentQuestionIndex]);

  // Listen for session state change to 'leaderboard' from teacher
  useEffect(() => {
    const effectiveGameState = activeSession?.gameState ?? gameState;
    if (effectiveGameState === 'leaderboard') {
      setShowPlayerResult(false);
      setShowLeaderboard(true);
    }
  }, [activeSession?.gameState, gameState]);

  useEffect(() => {
    if (isFinalScoresRoute) {
      setShowPlayerResult(false);
      setShowLeaderboard(true);
    }
  }, [isFinalScoresRoute]);

  useEffect(() => {
    setStudySession(getStoredEmpiricalStudySession(sessionId || undefined));
  }, [sessionId, isFinalScoresRoute]);

  useEffect(() => {
    if (
      !isFinalScoresRoute ||
      !activeSession?.empiricalStudyEnabled ||
      !studySession?.participantCode ||
      studySession.consentChoice !== 'consent' ||
      !activePlayer ||
      studySession.gameMetricsRecordedAt
    ) {
      return;
    }

    const completedAt = new Date().toISOString();
    const gameDurationSeconds = studySession.gameStartedAt
      ? Math.max(
          0,
          Math.round(
            (Date.now() - new Date(studySession.gameStartedAt).getTime()) / 1000
          )
        )
      : undefined;

    empiricalStudyService
      .recordGameCompletion(studySession.participantCode, {
        finalScore: activePlayer.score ?? 0,
        gameDurationSeconds
      })
      .then(() => {
        const updated = updateEmpiricalStudySession({
          sessionId: studySession.sessionId,
          gameCompletedAt: completedAt,
          gameDurationSeconds,
          finalScore: activePlayer.score ?? 0,
          gameMetricsRecordedAt: completedAt
        });
        setStudySession(updated);
      })
      .catch((metricError) => {
        console.error('❌ Failed to record empirical study game metrics:', metricError);
      });
  }, [
    isFinalScoresRoute,
    activeSession?.empiricalStudyEnabled,
    activePlayer,
    studySession
  ]);

  const handleReturnToLobby = () => {
    resetSessionStore();
    clearStoredSessionData();
    resetGame();
    navigate('/lobby');
  };

  const handleGoToPostGameSurvey = () => {
    if (!sessionId || !playerId) {
      return;
    }

    const params = new URLSearchParams();
    params.set('sessionId', sessionId);
    params.set('playerId', playerId);
    navigate(`/study/post-game?${params.toString()}`);
  };

  const handleAiContinue = () => {
    if (!isAiMode) {
      return;
    }

    if (isFinalScoresRoute) {
      handleReturnToLobby();
      return;
    }

    // First continue click transitions from result view to leaderboard
    if (showPlayerResult && !showLeaderboard) {
      setShowPlayerResult(false);
      setShowLeaderboard(true);
      return;
    }

    // Once on leaderboard, advance to next stage or final screen
    const nextStage = advanceToNextStage();

    if (!nextStage) {
      navigate('/final-scores');
      return;
    }

    if (nextStage.type === 'bonus') {
      navigate('/bonus');
      return;
    }

    if (nextStage.type === 'question') {
      navigate('/question');
      return;
    }

    // Fallback: default to question screen
    navigate('/question');
  };

  // If no current player found, something is wrong with initialization
  if (!activePlayer && !isSpectatorView) {
    console.error('❌ Current player not found in store. This should not happen after initialization.', {
      currentPlayerId,
      playerCount: players.length
    });
    return (
      <div style={{ padding: '2rem', textAlign: 'center', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <h2 style={{ color: '#F44336' }}>Error: Player data not found. Please restart the game.</h2>
      </div>
    );
  }

  // Calculate current round score with speed bonus (if applicable)
  const basePoints = isCorrect ? scoringConfig.basePoints : 0;
  const questionMultiplier = currentQuestion?.pointMultiplier ?? 1;
  const roundScore = activePlayer?.currentRoundScore || (basePoints * questionMultiplier);
  const speedBonusAmount = activePlayer ? roundScore - (basePoints * questionMultiplier) : 0;

  // Create leaderboard from real cumulative scores
  const playerHighlightId = (currentPlayerId ?? activePlayer?.id) ?? null;

  const leaderboardData = cumulativeScores.map((player, index) => ({
    playerId: player.playerId,
    name: player.name,
    totalScore: player.totalScore, // This comes from getCumulativeScores() which uses player.score
    roundScores: player.roundScores,
    rank: index + 1,
    isCurrentPlayer: playerHighlightId ? player.playerId === playerHighlightId : false
  }));

  if (!currentQuestion) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <h2>Loading results...</h2>
      </div>
    );
  }

  const answerThemes = [
    { base: '#F44336', accent: '#D32F2F', text: '#FFFFFF' },
    { base: '#2196F3', accent: '#1976D2', text: '#FFFFFF' },
    { base: '#4CAF50', accent: '#388E3C', text: '#FFFFFF' },
    { base: '#FFA726', accent: '#FB8C00', text: '#FFFFFF' }
  ];
  const getAnswerTheme = (index: number) => answerThemes[index] ?? { base: '#64748B', accent: '#475569', text: '#FFFFFF' };

  return (
    <>
    <MobileGameOverlay />
    <div style={{
      padding: '1rem 1.25rem 1.5rem',
      textAlign: 'center',
      minHeight: '100vh',
      background: `
        radial-gradient(circle at 15% 20%, rgba(148, 163, 184, 0.18) 0%, transparent 45%),
        radial-gradient(circle at 80% 15%, rgba(14, 165, 233, 0.2) 0%, transparent 55%),
        radial-gradient(circle at 20% 85%, rgba(34, 197, 94, 0.16) 0%, transparent 60%),
        linear-gradient(155deg, #0F172A 0%, #1E3A8A 45%, #0EA5E9 85%)
      `,
      color: '#E2E8F0',
      backgroundAttachment: 'fixed',
      overflowY: 'auto',
      position: 'relative'
    }}>
      {/* Question Progress */}
      <div style={{ position: 'absolute', top: '14px', left: '16px', color: 'rgba(226, 232, 240, 0.7)', fontSize: isScoreResultView ? resultScaledFontSize('0.95rem') : '0.95rem' }}>
        Question {currentQuestionIndex + 1} of {totalQuestions}
      </div>

      {/* Teacher Control Status */}
      {!showPlayerResult && !isAiMode && (
        <div style={{
          position: 'absolute',
          top: '14px',
          right: '16px',
          fontSize: '1rem',
          color: '#4CAF50',
          fontWeight: 'bold',
          textAlign: 'right'
        }}>
          <div>⏳ Waiting for teacher</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '2px' }}>
            to proceed to next round
          </div>
        </div>
      )}

      {!isSpectatorView && activePlayer && showPlayerResult && !showLeaderboard ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: 'auto',
          width: '100%',
          maxWidth: '640px',
          margin: '0 auto',
          paddingTop: '1.75rem',
          paddingBottom: '0.75rem',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          <h1 style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            fontSize: resultScaledFontSize('2.2rem'),
            color: isCorrect ? '#4CAF50' : '#F44336',
            margin: '0 0 0.35rem',
            fontWeight: 'bold'
          }}>
            <span style={{
              fontSize: resultScaledFontSize('2.5rem'),
              lineHeight: 1,
              display: 'inline-block',
              animation: isCorrect ? 'bounce 0.6s ease' : 'shake 0.6s ease'
            }}>
              {isCorrect ? '🎉' : '😔'}
            </span>
            <span>{isCorrect ? 'Correct!' : 'Wrong!'}</span>
          </h1>

          {/* Speed Bonus Indicator */}
          {speedBonusAmount > 0 && (
            <div style={{
              fontSize: resultScaledFontSize('0.85rem'),
              color: '#4CAF50',
              marginBottom: '0.25rem',
              fontWeight: 'bold'
            }}>
              🚀 Speed Bonus: +{speedBonusAmount} points!
            </div>
          )}

          {/* Question Multiplier Indicator */}
          {questionMultiplier > 1 && (
            <div style={{
              fontSize: resultScaledFontSize('0.85rem'),
              color: '#FF9800',
              marginBottom: '0.25rem',
              fontWeight: 'bold'
            }}>
              {questionMultiplier === 2 && '⭐ Double Points Question!'}
              {questionMultiplier === 3 && '🌟 Triple Points Question!'}
              {questionMultiplier > 3 && `✨ ${questionMultiplier}x Points Question!`}
            </div>
          )}

          <div style={{
            maxWidth: '460px',
            fontSize: resultScaledFontSize('0.95rem'),
            color: '#F8FAFC',
            lineHeight: '1.35',
            marginBottom: '0.8rem',
            textShadow: '0 2px 8px rgba(15, 23, 42, 0.6)'
          }}>
            {currentQuestion.text}
          </div>

          {/* Answer Options Review Section */}
          <div style={{
            maxWidth: '560px',
            margin: '0 auto',
            width: '100%',
            paddingBottom: '0.25rem'
          }}>
            <h3 style={{
              color: '#FFD700',
              fontSize: resultScaledFontSize('1rem'),
              marginBottom: '0.45rem',
              fontWeight: 'bold',
              textShadow: '0 2px 8px rgba(15, 23, 42, 0.6)'
            }}>
              Answer Options:
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.55rem',
              textAlign: 'left',
              marginBottom: '0.75rem'
            }}>
              {currentQuestion.choices.map((choice, index) => {
                const isCorrectAnswer = index === currentQuestion.correctAnswerIndex;
                const isUserSelection = index === playerSelectedAnswer;
                const isUserCorrect = playerSelectedAnswer === currentQuestion.correctAnswerIndex;
                const theme = getAnswerTheme(index);

                const baseCardStyle: CSSProperties = {
                  background: `linear-gradient(135deg, ${theme.base} 0%, ${theme.accent} 100%)`,
                  color: theme.text,
                  padding: '0.75rem 0.95rem',
                  borderRadius: '12px',
                  textAlign: 'left',
                  fontSize: resultScaledFontSize('1rem'),
                  fontWeight: 'bold',
                  border: `2px solid ${theme.accent}`,
                  boxShadow: '0 5px 14px rgba(0,0,0,0.2)',
                  transform: 'translateY(0)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  overflow: 'hidden'
                };

                const cardStyle: CSSProperties = { ...baseCardStyle };
                let primaryLabel: string | null = null;
                let primaryLabelColor = '#E2E8F0';
                let labelOffset = 0;
                let statusIcon: string | null = null;
                let statusIconColor = '#FFFFFF';

                if (isCorrectAnswer) {
                  cardStyle.border = '4px solid #4ADE80';
                  cardStyle.boxShadow = '0 0 24px rgba(74, 222, 128, 0.4)';
                  primaryLabel = isUserSelection ? 'Correct Answer ✓' : 'Correct Answer';
                  primaryLabelColor = '#4ADE80';
                  labelOffset = -10;
                  statusIcon = '✓';
                  statusIconColor = '#BBF7D0';
                }

                if (isUserSelection && !isUserCorrect) {
                  cardStyle.border = '4px solid #F87171';
                  cardStyle.boxShadow = '0 0 24px rgba(248, 113, 113, 0.4)';
                  primaryLabel = 'Your Choice';
                  primaryLabelColor = '#F87171';
                  labelOffset = -10;
                  statusIcon = '✕';
                  statusIconColor = '#FCA5A5';
                }

                if (isUserSelection && isUserCorrect && !isCorrectAnswer) {
                  primaryLabel = 'Your Choice ✓';
                  primaryLabelColor = '#4ADE80';
                  labelOffset = -10;
                  statusIcon = '✓';
                  statusIconColor = '#BBF7D0';
                  cardStyle.border = '4px solid #4ADE80';
                  cardStyle.boxShadow = '0 0 24px rgba(74, 222, 128, 0.4)';
                }

                return (
                  <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {primaryLabel && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.2rem 0.65rem',
                        alignSelf: 'flex-start',
                        marginLeft: `${labelOffset}px`,
                        borderRadius: '999px',
                        fontWeight: 700,
                        fontSize: resultScaledFontSize('0.75rem'),
                        letterSpacing: '0.01em',
                        backgroundColor: 'rgba(15, 23, 42, 0.6)',
                        border: `2px solid ${primaryLabelColor}`,
                        color: primaryLabelColor,
                        boxShadow: `0 8px 18px ${primaryLabelColor === '#4ADE80' ? 'rgba(74, 222, 128, 0.35)' : 'rgba(248, 113, 113, 0.35)'}`
                      }}>
                        {primaryLabel}
                      </div>
                    )}
                    <div style={cardStyle}>
                      {/* Option Letter */}
                      <div style={{
                        width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.base}, ${theme.accent})`,
                      border: `2px solid ${isCorrectAnswer ? '#A7F3D0' : theme.accent}`,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: resultScaledFontSize('1.05rem'),
                      fontWeight: 'bold',
                      boxShadow: '0 3px 8px rgba(0,0,0,0.3)'
                    }}>
                      {String.fromCharCode(65 + index)}
                    </div>

                    {/* Choice text */}
                    <div style={{ flex: 1, lineHeight: '1.25' }}>
                      {choice}
                    </div>

                    {/* Status Icon */}
                    {statusIcon && (
                      <div style={{
                        fontSize: resultScaledFontSize('1.45rem'),
                        color: statusIconColor,
                        flexShrink: 0
                      }}>
                        {statusIcon}
                      </div>
                    )}

                    {/* Status Badge removed in favor of external tag */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{
            marginBottom: '0.5rem',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {isAiMode ? (
              isFinalScoresRoute ? (
                <button
                  type="button"
                  onClick={handleReturnToLobby}
                  style={{
                    padding: '0.65rem 1.5rem',
                    fontSize: resultScaledFontSize('1rem'),
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.25)'
                  }}
                >
                  Back to Lobby
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleAiContinue}
                  style={{
                    padding: '0.65rem 1.5rem',
                    fontSize: resultScaledFontSize('1rem'),
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.25)'
                  }}
                >
                  Continue
                </button>
              )
            ) : null}
          </div>
        </div>
      ) : showLeaderboard ? (
        <div style={{ maxWidth: '760px', margin: '0 auto', paddingTop: '3.25rem' }}>
          <h1 style={{ marginBottom: '0.6rem', color: '#FFD700', fontSize: '2.2rem' }}>Leaderboard - Round {currentQuestionIndex + 1}</h1>

          {!isSpectatorView && activePlayer && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.55)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.28)',
              borderRadius: '14px',
              maxWidth: '420px',
              margin: '0 auto 1rem',
              padding: '0.85rem 1rem'
            }}>
              <div style={{
                fontSize: '1.05rem',
                color: '#FFD700',
                fontWeight: 'bold',
                marginBottom: '0.2rem'
              }}>
                +{roundScore} points this round
              </div>
              <div style={{
                fontSize: '0.95rem',
                color: '#E2E8F0'
              }}>
                Total Score: {activePlayer.score} points
              </div>
            </div>
          )}

          <div style={{
            fontSize: '0.95rem',
            color: 'rgba(226, 232, 240, 0.85)',
            marginBottom: '1.25rem'
          }}>
            {currentQuestionIndex === 0
              ? 'Scores from this round only'
              : `Cumulative scores from Rounds 1-${currentQuestionIndex + 1}`
            }
          </div>

          <div style={{
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.35)',
            padding: '1.25rem',
            borderRadius: '12px',
            maxWidth: '660px',
            margin: '0 auto 1.25rem',
            animation: 'fadeIn 0.5s ease-in'
          }}>
            {leaderboardData.map((player, index) => {
              const isCurrentPlayer = player.isCurrentPlayer;
              return (
                <div key={player.playerId || index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.9rem 1rem',
                  backgroundColor: index === 0 ? '#FFD700' : isCurrentPlayer ? '#2196F3' : '#555',
                  color: index === 0 || isCurrentPlayer ? '#000' : '#fff',
                  borderRadius: '12px',
                  marginBottom: '0.55rem',
                  transform: isCurrentPlayer ? 'scale(1.03)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  border: isCurrentPlayer ? '3px solid #FFD700' : 'none',
                  animation: index <= 2 ? `slideUp 0.5s ease ${index * 0.1}s both` : 'none'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                  }}>
                    <span style={{
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      {index === 0 && '🏆'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      #{player.rank} {player.name}
                      {isCurrentPlayer && ' (You)'}
                    </span>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      marginBottom: '0.15rem'
                    }}>
                      {player.totalScore} pts
                    </div>
                    {/* Show round breakdown for current player only if we have meaningful data */}
                    {isCurrentPlayer && player.roundScores && player.roundScores.filter(s => s > 0).length > 1 && (
                      <div style={{
                        fontSize: '0.72rem',
                        opacity: 0.8
                      }}>
                        {player.roundScores
                          .map((score, roundIndex) => ({ score, roundIndex }))
                          .filter(({ score }) => score !== undefined && score !== null && score > 0)
                          .map(({ score, roundIndex }, displayIndex, filteredArray) => (
                            <span key={roundIndex}>
                              Round {roundIndex + 1}: {score}
                              {displayIndex < filteredArray.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            marginBottom: '0.5rem',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {isAiMode ? (
              isFinalScoresRoute ? (
                <button
                  type="button"
                  onClick={handleReturnToLobby}
                  style={{
                    padding: '0.65rem 1.5rem',
                    fontSize: '1rem',
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.25)'
                  }}
                >
                  Back to Lobby
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleAiContinue}
                  style={{
                    padding: '0.65rem 1.5rem',
                    fontSize: '1rem',
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.25)'
                  }}
                >
                  Continue
                </button>
              )
            ) : isFinalStage ? (
              isFinalScoresRoute &&
              activeSession?.empiricalStudyEnabled &&
              studySession?.participantCode &&
              studySession.consentChoice === 'consent' &&
              !studySession.postSurveySubmittedAt ? (
                <button
                  type="button"
                  onClick={handleGoToPostGameSurvey}
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    backgroundColor: '#FF6B35',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 6px 20px rgba(255,107,53,0.25)'
                  }}
                >
                  Take me to the post-game survey
                </button>
              ) : isFinalScoresRoute &&
                activeSession?.empiricalStudyEnabled &&
                studySession?.consentChoice === 'consent' &&
                studySession?.postSurveySubmittedAt ? (
                <span style={{
                  fontSize: '1.05rem',
                  color: '#86EFAC',
                  fontWeight: 'bold'
                }}>
                  Post-game survey submitted. Thank you.
                </span>
              ) : (
              <span style={{
                fontSize: '1.1rem',
                color: '#FF6B35',
                fontWeight: 'bold'
              }}>
                Wait for your teacher to proceed...
              </span>
              )
            ) : (
              <span style={{
                fontSize: '1.1rem',
                color: '#4CAF50',
                fontWeight: 'bold'
              }}>
                Waiting for teacher to start the next question...
              </span>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '2rem', animation: 'pulse 2s ease-in-out infinite' }}>
            ⏳
          </div>
          <h2 style={{ color: '#4CAF50', fontSize: '2rem', marginBottom: '1rem' }}>
            Waiting for teacher...
          </h2>
          <p style={{ color: 'rgba(226, 232, 240, 0.8)', fontSize: '1.2rem' }}>
            Your teacher will show the leaderboard when ready
          </p>
        </div>
      )}


      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-20px); }
          60% { transform: translateY(-10px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
      `}</style>
    </div>
    </>
  );
};

export default ScoreScreen;
